import type { UseSignedInDeps } from '@/types/session/sessionTypes'
import { readWeb3Address } from '#shared/utils/web3Address'
import { isSignedInWithWallet } from '../core/sessionWallet'

/** Watches the connected wallet through the wallet stack, loading it first. */
function watchWithWagmi() {
  const { $loadWagmi } = useNuxtApp()
  return async (onChange: (address: string | null) => void) => {
    const config = await $loadWagmi()
    const { getConnection, watchConnection } = await import('@wagmi/core')
    onChange(getConnection(config).address ?? null)
    return watchConnection(config, { onChange: connection => onChange(connection.address ?? null) })
  }
}

/**
 * Whether the visitor is signed in the way the header shows it: a Supabase session signed
 * with the wallet that is connected now.
 *
 * Only a session signed with a wallet can pass that check, so the wallet stack is loaded only
 * once there is one. Everyone else reads as signed out without it.
 */
export function useSignedIn(deps: UseSignedInDeps = {}) {
  const getUser = deps.getUser ?? (() => useSupabaseUser().value)
  const connectedAddress = shallowRef<string | null>(null)

  if (import.meta.client) {
    const watchConnectedAddress = deps.watchConnectedAddress ?? watchWithWagmi()
    let started = false
    let disposed = false
    let stop: (() => void) | null = null

    watch(() => readWeb3Address(getUser()?.app_metadata) != null, (hasWalletSession) => {
      if (!hasWalletSession || started)
        return
      started = true
      watchConnectedAddress((address) => {
        connectedAddress.value = address
      })
        .then((unwatch) => {
          if (disposed)
            unwatch()
          else
            stop = unwatch
        })
        .catch((error: unknown) => {
          console.error('[session] Failed to watch the connected wallet:', error)
        })
    }, { immediate: true })

    onScopeDispose(() => {
      disposed = true
      stop?.()
    })
  }

  const isSignedIn = computed(() => isSignedInWithWallet(getUser(), connectedAddress.value))

  return { isSignedIn }
}
